import { CheckCircle } from 'lucide-react';
import type { AnswerCardProps, Question } from '../types';
import { typeNames, subTypeNames } from '../../../constants/questionTypes';

const getQuestionTypeName = (type: string, subType?: string) => {
  if (type === 'scratch_coding') return 'Scratch编程题';
  if (type === 'python_coding') return 'Python编程题';
  if (type === 'cpp_coding') return 'C++编程题';
  if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
  return typeNames[type] || '单选题';
};

const groupQuestionsByType = (examQuestions: Question[]) => {
  const groups: { type: string; name: string; indices: number[] }[] = [];
  // 与 PaperEditor 保持一致的排序：Python 在前，Scratch 在后
  const typeOrder = ['single_choice', 'judge', 'multi_choice', 'python_coding', 'cpp_coding', 'scratch_coding', 'coding'];
  examQuestions.forEach((q, idx) => {
    let groupType = q.type;
    if (q.type === 'coding' && q.subType) groupType = `${q.subType}_coding`;
    const existingGroup = groups.find(g => g.type === groupType);
    if (existingGroup) existingGroup.indices.push(idx);
    else groups.push({ type: groupType, name: getQuestionTypeName(q.type, q.subType), indices: [idx] });
  });
  return groups.sort((a, b) => {
    const indexA = typeOrder.indexOf(a.type);
    const indexB = typeOrder.indexOf(b.type);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

const getQuestionStatus = (
  idx: number,
  examQuestions: Question[],
  answers: Record<string, string>,
  isSubmitted: boolean,
  revealedQuestions?: Set<string>,
  isPracticeMode?: boolean
) => {
  const q = examQuestions[idx];
  if (!q) return 'unanswered';

  // 练习模式：使用逐题揭示
  if (isPracticeMode && revealedQuestions) {
    const isRevealed = revealedQuestions.has(q.id);
    if (isRevealed) {
      const ans = answers[q.id];
      if (!ans) return 'wrong';
      if (q.type === 'multi_choice') {
        const selectedIds = ans.split(',');
        const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
        const isCorrect = selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id));
        return isCorrect ? 'correct' : 'wrong';
      }
      if (q.type === 'coding' || q.type === 'scratch_coding') {
        const aiScore = parseFloat(answers[`${q.id}_aiScore`] || '0');
        return aiScore >= q.score * 0.6 ? 'correct' : 'wrong';
      }
      const selectedOption = q.options?.find(o => o.id === ans);
      return selectedOption?.isCorrect ? 'correct' : 'wrong';
    }
    return answers[q.id] ? 'answered' : 'unanswered';
  }

  // 正常模式：使用全局 isSubmitted
  if (isSubmitted) {
    const ans = answers[q.id];
    if (!ans) return 'wrong';
    if (q.type === 'multi_choice') {
      const selectedIds = ans.split(',');
      const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
      const isCorrect = selectedIds.length === correctIds.length && selectedIds.every(id => correctIds.includes(id));
      return isCorrect ? 'correct' : 'wrong';
    }
    if (q.type === 'coding' || q.type === 'scratch_coding') {
      const aiScore = parseFloat(answers[`${q.id}_aiScore`] || '0');
      return aiScore >= q.score * 0.6 ? 'correct' : 'wrong';
    }
    const selectedOption = q.options?.find(o => o.id === ans);
    return selectedOption?.isCorrect ? 'correct' : 'wrong';
  }
  return answers[q.id] ? 'answered' : 'unanswered';
};

export default function AnswerCard({
  examQuestions,
  currentIndex,
  answers,
  markedQuestions,
  isSubmitted,
  paper,
  revealedQuestions,
  isPracticeMode,
  onQuestionSelect,
  onCloseSidebar,
  onGroupScoreChange
}: AnswerCardProps & { onGroupScoreChange?: (groupType: string, newScorePerQuestion: number) => void }) {
  const groupedQuestions = groupQuestionsByType(examQuestions);
  const typeLabels = ['一', '二', '三', '四', '五'];
  const answeredCount = Object.keys(answers).filter(key => !key.includes('_')).length;
  const markedCount = markedQuestions.size;

  const getGroupTotalScore = (group: { type: string; name: string; indices: number[] }) =>
    group.indices.reduce((sum, idx) => sum + (examQuestions[idx]?.score || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold flex items-center gap-2 text-slate-700">
            <CheckCircle className="w-4 h-4" />答题卡
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="text-gray-600">
              已答: <span className="font-bold text-indigo-600">{answeredCount}</span>/{examQuestions.length}
            </span>
            <span className="text-gray-600">
              标记: <span className="font-bold text-amber-600">{markedCount}</span>
            </span>
          </div>
          <span className="text-gray-600 text-base">
            总分: <span className="font-bold text-green-600">{paper?.total_score || 0}</span>分
          </span>
        </div>
      </div>
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {groupedQuestions.map((group, groupIdx) => {
          const groupTotalScore = getGroupTotalScore(group);
          const questionCount = group.indices.length;
          // 计算每题平均分（用于下拉框默认值）
          const avgScorePerQuestion = questionCount > 0 ? Math.round(groupTotalScore / questionCount) : 0;
          // 检查该题型下所有题目分值是否一致
          const allScoresSame = group.indices.every(idx => examQuestions[idx]?.score === examQuestions[group.indices[0]]?.score);
          const scoreOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 30];
          // 只有当所有题目分值一致且平均值在选项中时，才设置下拉框默认值
          const selectValue = allScoresSame && scoreOptions.includes(avgScorePerQuestion) ? String(avgScorePerQuestion) : '';

          return (
            <div key={group.type} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold text-gray-800">
                  {typeLabels[groupIdx]}、{group.name}（共{questionCount}题，{groupTotalScore}分）
                </span>
                {onGroupScoreChange && (
                  <select
                    value={selectValue}
                    onChange={(e) => onGroupScoreChange(group.type, parseInt(e.target.value))}
                    className="px-2 py-0.5 text-xs border border-gray-300 rounded bg-white hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    title={allScoresSame ? "设置每题分值" : "该题型题目分值不一致，请选择统一分值"}
                  >
                    <option value="" disabled>-</option>
                    {scoreOptions.map(score => (
                      <option key={score} value={score}>{score}分</option>
                    ))}
                  </select>
                )}
              </div>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {group.indices.map((idx) => {
                const status = getQuestionStatus(idx, examQuestions, answers, isSubmitted, revealedQuestions, isPracticeMode);
                const isCurrent = idx === currentIndex;
                const isMarked = markedQuestions.has(idx);
                const isAnswered = status === 'answered' || status === 'correct' || status === 'wrong';

                // 练习模式或已提交：显示对错结果
                const showResult = (isPracticeMode && revealedQuestions?.has(examQuestions[idx]?.id)) || isSubmitted;
                if (showResult) {
                  const isCorrect = status === 'correct';
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        onQuestionSelect(idx);
                        onCloseSidebar?.();
                      }}
                      className={`aspect-square w-full text-sm font-medium rounded-lg border-2 flex items-center justify-center ${
                        isCorrect
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-red-500 text-white border-red-500'
                      }`}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </button>
                  );
                }

                return (
                  <button
                    key={idx}
                    onClick={() => {
                      onQuestionSelect(idx);
                      onCloseSidebar?.();
                    }}
                    className={`aspect-square w-full text-sm font-medium rounded-lg border-2 transition-all flex items-center justify-center ${
                      isCurrent
                        ? 'border-red-500 ' + (isAnswered ? 'bg-blue-500 text-white' : 'bg-white text-gray-900')
                        : isAnswered
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-900 border-gray-300 hover:bg-blue-50'
                    }`}
                  >
                    {isMarked ? <span className="text-lg font-black">?</span> : String(idx + 1).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      </div>
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        {isSubmitted || isPracticeMode ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 border-2 border-green-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                01
              </div>
              <span className="text-gray-600">答对</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-500 border-2 border-red-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                02
              </div>
              <span className="text-gray-600">答错/未答</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center text-white text-xs font-medium">
                01
              </div>
              <span className="text-gray-600">已答</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border border-gray-300 rounded-lg flex items-center justify-center text-gray-900 text-xs font-medium">
                03
              </div>
              <span className="text-gray-600">未答</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border-2 border-red-500 rounded-lg flex items-center justify-center text-gray-900 text-xs font-medium">
                08
              </div>
              <span className="text-gray-600">当前题目</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-white border-2 border-amber-500 rounded-lg flex items-center justify-center text-amber-600 text-xs font-medium">
                ？
              </div>
              <span className="text-gray-600">已标记</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}